/**
 * ⚠️ CE FICHIER VIT DANS `src/partage/`, ET CE N'EST PAS UN RANGEMENT ARBITRAIRE.
 * Il est lu par le SERVICE (`src/modules/foncier.ts`) et par l'INTERFACE
 * (`frontend/src/effet.tsx`). Il était d'abord dans `src/domaine/`, importé
 * depuis le front par `../../src/domaine/quotites.js` : ça compilait en local
 * — l'arbre entier est là — et le **build Docker échouait**, parce que l'étage
 * `interface` ne copie que `frontend/`. Le défaut ne se voyait qu'au
 * déploiement.
 *
 * Un dossier `partage/` explicite, copié dans les deux étages du Dockerfile,
 * rend la dépendance visible au lieu de la faire deviner à un chemin relatif.
 *
 * ⚠️ ET IL EST SOUS `src/`, PAS À LA RACINE. Le `rootDir` du service vaut
 * `src` : un dossier voisin casse la disposition de sortie (`dist/src/index.js`
 * au lieu de `dist/index.js`), et le runner résout `../frontend/dist` depuis
 * `dist/` — l'interface cesserait d'être servie. Mesuré : TS6059.
 *
 * ⚠️ RIEN D'AUTRE QUE DU CALCUL PUR N'ENTRE ICI : pas de `pg`, pas de `config`,
 * pas de `logger`. Ce fichier part dans le bundle du navigateur.
 *
 * SOMME EXACTE DE QUOTITÉS (§9).
 *
 * ⚠️ EN FRACTIONS ENTIÈRES, JAMAIS EN FLOTTANT — et ce n'est pas de la
 * coquetterie. Trois héritiers à parts égales font 1/3 chacun : `0.333 × 3`
 * vaut 0,999, et l'écran afficherait « 99,9 % — il manque une part » sur une
 * indivision parfaitement close. À l'inverse, `1/2 + 1/2 + 1/3` vaut 1,333 et
 * doit se lire « 4/3 du tout », pas « environ 133 % ».
 *
 * ⚠️ ON REPRODUIT LA MATHÉMATIQUE DE LA GARDE, PAS UNE APPROXIMATION. La
 * migration 006 a corrigé exactement ce défaut en base : sa première version
 * ramenait tout au plus grand dénominateur puis divisait en ENTIERS, ce qui
 * faisait valoir 1 à `1 * 4 / 3`. Le dénominateur commun correct est le PPCM.
 * Si l'écran calculait autrement que la garde, il annoncerait conforme ce que
 * la base refuse — ou l'inverse, ce qui est pire : l'agent corrigerait un
 * dossier qui n'a rien.
 */

export interface Fraction {
  num: bigint;
  den: bigint;
}

const pgcd = (a: bigint, b: bigint): bigint => (b === 0n ? (a < 0n ? -a : a) : pgcd(b, a % b));
const ppcm = (a: bigint, b: bigint): bigint => (a / pgcd(a, b)) * b;

export interface SommeQuotites {
  /** Numérateur et dénominateur de la somme, réduits. */
  num: number;
  den: number;
  /** `1/1` exactement — l'indivision est close. */
  complet: boolean;
  /** Au-delà du tout : la base refuse cet état (`FON_QUOTITE_EXCEDENTAIRE`). */
  excedentaire: boolean;
  /** Ce qui reste à attribuer, réduit. `null` si excédentaire. */
  reste: { num: number; den: number } | null;
  /** Nombre de parts sommées. 0 ⇒ rien à conclure, pas « zéro pour cent ». */
  parts: number;
}

/**
 * ⚠️ ZÉRO PART NE VAUT PAS ZÉRO POUR CENT. Une parcelle sans aucun droit de
 * propriété n'est pas « détenue à 0 % » : elle n'est pas encore immatriculée.
 * `parts: 0` oblige l'appelant à distinguer les deux — c'est le même canari que
 * `fon.garde_quotites`, qui sort sans conclure quand il n'y a rien à contredire.
 */
export function sommerQuotites(
  fractions: Array<{ num: number; den: number }>,
): SommeQuotites {
  if (fractions.length === 0) {
    return { num: 0, den: 1, complet: false, excedentaire: false, reste: null, parts: 0 };
  }
  let commun = 1n;
  for (const f of fractions) commun = ppcm(commun, BigInt(f.den));

  let total = 0n;
  for (const f of fractions) total += BigInt(f.num) * (commun / BigInt(f.den));

  const d = pgcd(total, commun) || 1n;
  const num = total / d;
  const den = commun / d;

  const excedentaire = total > commun;
  let reste: { num: number; den: number } | null = null;
  if (!excedentaire) {
    const rn = commun - total;
    const rd = pgcd(rn, commun) || 1n;
    reste = { num: Number(rn / rd), den: Number(commun / rd) };
  }
  return {
    num: Number(num), den: Number(den),
    complet: total === commun,
    excedentaire,
    reste,
    parts: fractions.length,
  };
}

/**
 * LA QUOTITÉ, ÉCRITE POUR UN ACTE D'ÉTAT.
 *
 * ⚠️⚠️ TROUVÉ EN REGARDANT UNE COPIE DE TITRE IMPRIMÉE, pas en relisant le code.
 * Le papier portait :
 *
 *     Hypothèque · Banque Islamique de Guinée · PLEINE PROPRIÉTÉ · rang 7
 *
 * Une hypothèque de 1/1 grève la TOTALITÉ du fonds — elle n'en donne pas la
 * propriété. Trois fonctions locales écrivaient `num === den ? 'pleine
 * propriété' : 'n/d'`, chacune dans son module : `documents.ts` deux fois,
 * `livre.ts` une. Aucune ne connaissait la nature du droit, et le raccourci est
 * juste pour la seule propriété. Sur un titre foncier, dire qu'un créancier
 * hypothécaire détient la pleine propriété n'est pas une approximation.
 *
 * ⚠️ UNE SEULE IMPLÉMENTATION, ICI. Trois copies de la même formule divergent
 * au premier correctif appliqué à une seule — et celle qu'on oublie est celle
 * qui s'imprime.
 */
const NATURES_DE_PROPRIETE = new Set(['propriete', 'nue_propriete']);

export function libelleQuotite(num: unknown, den: unknown, nature?: unknown): string | null {
  if (num === null || num === undefined || den === null || den === undefined) return null;
  const n = Number(num); const d = Number(den);
  if (!Number.isFinite(n) || !Number.isFinite(d) || d === 0) return null;
  if (n !== d) return `${n}/${d}`;
  // Le tout : le mot change selon ce que le droit EST.
  return NATURES_DE_PROPRIETE.has(String(nature ?? 'propriete'))
    ? 'pleine propriété'
    : 'la totalité du fonds';
}
